import { memo } from 'react';
import {
  SupplyBorrowChart,
  SupplyBorrowLegend,
  SupplyBorrowChartType,
  Text,
} from '@cronos-labs/tectonic-ui-kit';
import { TectonicAsset } from '@cronos-labs/tectonic-sdk/dist/types';
import { BigNumber } from 'ethers';
import { useMemo } from 'react';

import { Mode } from '@components/MarketsPageView/types';
import Card, { CardProps } from '@components/Card';
import { formatPercent, formatTotalUnderlyingAmount } from '@lib/units';
import useMarketHistory from '@hooks/useMarketHistory';
import Features from '@components/Features';
import useIsMobile from '@hooks/useIsMobile';
import useBorrowApy from '@hooks/useBorrowApy';

import BorrowSupplyCardDistributionApy from './BorrowSupplyCardDistributionApy';
import CardValue from './CardValue';

export interface BorrowCardProps {
  mode: Mode;
  amount: Null<BigNumber>;
  asset: TectonicAsset;
  cardProps?: Omit<CardProps, 'children'>;
}
function BorrowCard({
  mode,
  amount,
  asset,
  cardProps,
}: BorrowCardProps): JSX.Element {
  const isMobile = useIsMobile();
  const { borrowApy, tonicBorrowApy, partnerTokenBorrowApy, isLoading } =
    useBorrowApy(mode, asset);

  const { data: marketInfo } = useMarketHistory(asset.symbol, mode);
  const borrowData = useMemo(() => {
    return marketInfo?.map((mi) => {
      return {
        apy: mi.borrowApyPercent,
        totalAmount: mi.totalBorrowUsd,
        date: mi.timestamp,
      };
    });
  }, [marketInfo]);
  return (
    <Card
      variant={isMobile ? 'no-background' : 'default'}
      border={!isMobile}
      padding={isMobile ? 'none' : 'default'}
      {...(cardProps ?? {})}
    >
      <Text variant="heading3" className="mb-3">
        Borrow
      </Text>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Text className="text-small">Total borrow</Text>
          <CardValue
            loading={isLoading}
            value={
              amount !== null && (
                <Text className="text-large">
                  {formatTotalUnderlyingAmount(asset, amount)}
                </Text>
              )
            }
          />
        </div>
        <div className="space-y-1">
          <Text className="text-small">Borrow APY</Text>
          <CardValue
            loading={isLoading}
            value={
              borrowApy !== undefined && (
                <Text className="text-large">{formatPercent(borrowApy)}</Text>
              )
            }
          />
        </div>
        <BorrowSupplyCardDistributionApy
          distributionApy={tonicBorrowApy}
          loading={isLoading}
        />
        {Object.entries(partnerTokenBorrowApy).map(
          ([symbol, apy]) =>
            apy > 0 && (
              <BorrowSupplyCardDistributionApy
                key={symbol}
                distributionApy={apy}
                loading={isLoading}
                symbol={symbol}
              />
            )
        )}
      </div>
      {borrowData && !!borrowData.length && (
        <Features.MarketInsights>
          <div className="h-[200px] w-full space-y-4">
            <SupplyBorrowChart
              type={SupplyBorrowChartType.borrow}
              data={borrowData}
            />
          </div>
          <div className="pt-4">
            <SupplyBorrowLegend type={SupplyBorrowChartType.borrow} />
          </div>
        </Features.MarketInsights>
      )}
    </Card>
  );
}

export default memo(BorrowCard);
